import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Users, UserPlus, Edit, Trash2, Share2, X } from 'lucide-react';

const UsuariosSistemaPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = user?.tipo === 'sub_admin';

  const [usuarios, setUsuarios] = useState([]);
  const [locacoes, setLocacoes] = useState([]);
  const [despesasFixas, setDespesasFixas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modais
  const [modalCriar, setModalCriar] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalCompartilhar, setModalCompartilhar] = useState(false);
  
  // Dados dos formulários
  const [novoUsuario, setNovoUsuario] = useState({
    username: '',
    email: '',
    senha: '',
    nome_completo: '',
    observacoes: '',
    modelo_remuneracao: 'fixo',
    valor_fixo_por_moto: '',
    percentual_repasse: '',
    semanas_minimas: 4,
    regras_mecanico: false,
    despesas_fixas_personalizadas: []
  });
  
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [usuarioCompartilhando, setUsuarioCompartilhando] = useState(null);
  const [locacoesCompartilhadas, setLocacoesCompartilhadas] = useState([]);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [usuariosData, locacoesData, despesasData] = await Promise.all([
        apiService.listarUsuariosSistema(),
        apiService.getLocacoes(),
        apiService.api.get('/despesas-fixas').then(r => r.data).catch(() => ({ despesas: [] }))
      ]);
      
      setUsuarios(usuariosData.usuarios || []);
      setLocacoes(locacoesData || []);
      setDespesasFixas(despesasData.despesas || []);
      setError('');
    } catch (err) {
      setError('Erro ao carregar dados: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCriarUsuario = async (e) => {
    e.preventDefault();
    
    // Validação do modelo
    if (novoUsuario.modelo_remuneracao === 'fixo') {
      if (!novoUsuario.valor_fixo_por_moto || parseFloat(novoUsuario.valor_fixo_por_moto) <= 0) {
        setError('Informe o valor fixo por moto (maior que zero)');
        return;
      }
    } else if (novoUsuario.modelo_remuneracao === 'percentual') {
      const p = parseFloat(novoUsuario.percentual_repasse);
      if (!p || p <= 0 || p > 100) {
        setError('Informe um percentual de repasse válido (entre 0 e 100)');
        return;
      }
    }
    
    try {
      setLoading(true);
      const payload = {
        ...novoUsuario,
        valor_fixo_por_moto: parseFloat(novoUsuario.valor_fixo_por_moto || 0),
        percentual_repasse: parseFloat(novoUsuario.percentual_repasse || 0),
        semanas_minimas: parseInt(novoUsuario.semanas_minimas || 4),
        despesas_fixas_personalizadas: (novoUsuario.despesas_fixas_personalizadas || [])
          .filter(d => d.nome && d.nome.trim())
          .map(d => ({ nome: d.nome.trim(), valor: parseFloat(d.valor || 0), descricao: d.descricao || '' }))
      };
      const resultado = await apiService.criarUsuarioSistema(payload);
      
      if (resultado.sucesso) {
        setSuccess(`Usuário ${novoUsuario.username} criado com sucesso!`);
        setModalCriar(false);
        setNovoUsuario({
          username: '',
          email: '',
          senha: '',
          nome_completo: '',
          observacoes: '',
          modelo_remuneracao: 'fixo',
          valor_fixo_por_moto: '',
          percentual_repasse: '',
          semanas_minimas: 4,
          regras_mecanico: false,
          despesas_fixas_personalizadas: []
        });
        await carregarDados();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Erro ao criar usuário: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAtualizarUsuario = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const dados = { ...usuarioEditando };
      delete dados.id;
      delete dados.username;
      delete dados.tipo;
      delete dados.db_name;
      
      // Converter campos numéricos
      if (dados.valor_fixo_por_moto !== undefined && dados.valor_fixo_por_moto !== '') {
        dados.valor_fixo_por_moto = parseFloat(dados.valor_fixo_por_moto || 0);
      }
      if (dados.percentual_repasse !== undefined && dados.percentual_repasse !== '') {
        dados.percentual_repasse = parseFloat(dados.percentual_repasse || 0);
      }
      if (dados.semanas_minimas !== undefined && dados.semanas_minimas !== '') {
        dados.semanas_minimas = parseInt(dados.semanas_minimas || 4);
      }

      // Normalizar despesas fixas personalizadas
      if (Array.isArray(dados.despesas_fixas_personalizadas)) {
        dados.despesas_fixas_personalizadas = dados.despesas_fixas_personalizadas
          .filter(d => d.nome && d.nome.trim())
          .map(d => ({
            nome: d.nome.trim(),
            valor: parseFloat(d.valor || 0),
            descricao: d.descricao || ''
          }));
      }

      // Normalizar dados_cobranca (3 campos vazios = remover)
      if (dados.dados_cobranca && typeof dados.dados_cobranca === 'object') {
        const dc = dados.dados_cobranca;
        dados.dados_cobranca = {
          chave_pix: (dc.chave_pix || '').trim(),
          nome_beneficiario: (dc.nome_beneficiario || '').trim(),
          nome_banco: (dc.nome_banco || '').trim()
        };
      }
      
      const resultado = await apiService.atualizarUsuarioSistema(usuarioEditando.id, dados);
      
      if (resultado.sucesso) {
        setSuccess('Usuário atualizado com sucesso!');
        setModalEditar(false);
        setUsuarioEditando(null);
        await carregarDados();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Erro ao atualizar usuário: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeletarUsuario = async (usuario) => {
    if (!window.confirm(`Tem certeza que deseja deletar o usuário ${usuario.username}?\n\nIsso irá DELETAR o banco de dados ${usuario.db_name} e TODOS os dados deste usuário!`)) {
      return;
    }
    
    try {
      setLoading(true);
      const resultado = await apiService.deletarUsuarioSistema(usuario.id);
      
      if (resultado.sucesso) {
        setSuccess(`Usuário ${usuario.username} deletado!`);
        await carregarDados();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Erro ao deletar usuário: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const abrirModalCompartilhar = async (usuario) => {
    try {
      setUsuarioCompartilhando(usuario);
      setLoading(true);
      
      // Usar username em vez de id para buscar permissões
      const resultado = await apiService.listarLocacoesCompartilhadas(usuario.username);
      setLocacoesCompartilhadas(resultado.locacoes_permitidas || []);
      setModalCompartilhar(true);
    } catch (err) {
      setError('Erro ao carregar locações compartilhadas: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCompartilharLocacao = async (locacaoId) => {
    try {
      setLoading(true);
      // Usar username em vez de id
      const resultado = await apiService.compartilharLocacao(usuarioCompartilhando.username, locacaoId);
      
      if (resultado.sucesso) {
        setSuccess('Locação compartilhada com sucesso!');
        const resultadoAtualizado = await apiService.listarLocacoesCompartilhadas(usuarioCompartilhando.username);
        setLocacoesCompartilhadas(resultadoAtualizado.locacoes_permitidas || []);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Erro ao compartilhar locação: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoverCompartilhamento = async (locacaoId) => {
    try {
      setLoading(true);
      // Usar username em vez de id
      const resultado = await apiService.removerCompartilhamentoLocacao(usuarioCompartilhando.username, locacaoId);
      
      if (resultado.sucesso) {
        setSuccess('Compartilhamento removido!');
        const resultadoAtualizado = await apiService.listarLocacoesCompartilhadas(usuarioCompartilhando.username);
        setLocacoesCompartilhadas(resultadoAtualizado.locacoes_permitidas || []);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Erro ao remover compartilhamento: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const locacaoEstaCompartilhada = (locacaoId) => {
    // Verificar se a locação está na lista de IDs permitidos
    return locacoesCompartilhadas.includes(locacaoId);
  };

  if (loading && usuarios.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="h-8 w-8" />
            Gerenciar Usuários do Sistema
          </h1>
          <p className="text-gray-600 mt-2">
            Crie sub-admins e compartilhe locações específicas
          </p>
        </div>
        <Button onClick={() => setModalCriar(true)} className="flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          Novo Usuário
        </Button>
      </div>

      {/* Alertas */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Lista de Usuários */}
      <div className="grid gap-4">
        {usuarios.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              Nenhum sub-admin cadastrado. Clique em &ldquo;Novo Usuário&rdquo; para começar.
            </CardContent>
          </Card>
        ) : (
          usuarios.map((usuario) => (
            <Card key={usuario.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl">{usuario.nome_completo}</CardTitle>
                    <div className="text-sm text-gray-600 mt-1">
                      <div>@{usuario.username} • {usuario.email}</div>
                      <div className="mt-1">
                        <span className="font-semibold">Banco:</span> {usuario.db_name}
                      </div>
                      {usuario.modelo_remuneracao ? (
                        <div className="mt-2 inline-flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full">
                            {usuario.modelo_remuneracao === 'fixo'
                              ? `Modelo: Fixo R$ ${Number(usuario.valor_fixo_por_moto || 0).toFixed(2)}/moto`
                              : `Modelo: ${usuario.percentual_repasse || 0}% sobre líquido`}
                          </span>
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                            mín. {usuario.semanas_minimas || 4} sem.
                          </span>
                          {usuario.regras_mecanico && (
                            <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full">
                              Regras de mecânico
                            </span>
                          )}
                          {((Array.isArray(usuario.despesas_fixas_atribuidas) && usuario.despesas_fixas_atribuidas.length > 0) ||
                            (Array.isArray(usuario.despesas_fixas_personalizadas) && usuario.despesas_fixas_personalizadas.length > 0)) && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                              {(usuario.despesas_fixas_atribuidas?.length || 0) + (usuario.despesas_fixas_personalizadas?.length || 0)} despesa(s) fixa(s)
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="mt-2">
                          <span className="px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">
                            ⚠ Sem modelo de remuneração definido
                          </span>
                        </div>
                      )}
                      {usuario.observacoes && (
                        <div className="mt-1 text-gray-500">
                          {usuario.observacoes}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => abrirModalCompartilhar(usuario)}
                      className="flex items-center gap-1"
                    >
                      <Share2 className="h-4 w-4" />
                      Compartilhar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setUsuarioEditando({
                          ...usuario,
                          dados_cobranca: usuario.dados_cobranca || { chave_pix: '', nome_beneficiario: '', nome_banco: '' }
                        });
                        setModalEditar(true);
                      }}
                      className="flex items-center gap-1"
                    >
                      <Edit className="h-4 w-4" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeletarUsuario(usuario)}
                      className="flex items-center gap-1"
                    >
                      <Trash2 className="h-4 w-4" />
                      Deletar
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))
        )}
      </div>

      {/* Modal Criar Usuário */}
      <Dialog open={modalCriar} onOpenChange={setModalCriar}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Criar Novo Sub-Admin / Investidor</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCriarUsuario} className="space-y-4">
            <div>
              <Label>Username *</Label>
              <Input
                data-testid="input-username"
                required
                value={novoUsuario.username}
                onChange={(e) => setNovoUsuario({ ...novoUsuario, username: e.target.value })}
                placeholder="usuario123"
              />
            </div>
            
            <div>
              <Label>Nome Completo *</Label>
              <Input
                required
                value={novoUsuario.nome_completo}
                onChange={(e) => setNovoUsuario({ ...novoUsuario, nome_completo: e.target.value })}
                placeholder="João Silva"
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                required
                type="email"
                value={novoUsuario.email}
                onChange={(e) => setNovoUsuario({ ...novoUsuario, email: e.target.value })}
                placeholder="usuario@exemplo.com"
              />
            </div>
            
            <div>
              <Label>Senha *</Label>
              <Input
                required
                type="password"
                value={novoUsuario.senha}
                onChange={(e) => setNovoUsuario({ ...novoUsuario, senha: e.target.value })}
                placeholder="Senha forte"
              />
            </div>
            
            <div>
              <Label>Observações</Label>
              <Input
                value={novoUsuario.observacoes}
                onChange={(e) => setNovoUsuario({ ...novoUsuario, observacoes: e.target.value })}
                placeholder="Informações adicionais (opcional)"
              />
            </div>

            {/* === Modelo de Remuneração === */}
            <div className="border-t pt-4 space-y-4">
              <h4 className="font-semibold text-base">Modelo de Remuneração *</h4>

              <div>
                <Label>Tipo de Modelo *</Label>
                <select
                  data-testid="select-modelo-remuneracao"
                  required
                  value={novoUsuario.modelo_remuneracao}
                  onChange={(e) => setNovoUsuario({ ...novoUsuario, modelo_remuneracao: e.target.value })}
                  className="w-full border rounded px-3 py-2 bg-white"
                >
                  <option value="fixo">Pagamento fixo por moto</option>
                  <option value="percentual">Percentual sobre faturamento líquido</option>
                </select>
              </div>

              {novoUsuario.modelo_remuneracao === 'fixo' && (
                <>
                  <div>
                    <Label>Valor fixo por moto (R$) *</Label>
                    <Input
                      data-testid="input-valor-fixo"
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={novoUsuario.valor_fixo_por_moto}
                      onChange={(e) => setNovoUsuario({ ...novoUsuario, valor_fixo_por_moto: e.target.value })}
                      placeholder="1000.00"
                    />
                  </div>
                  <div>
                    <Label>Semanas mínimas pagas para receber valor fixo *</Label>
                    <Input
                      type="number"
                      min="1"
                      max="52"
                      required
                      value={novoUsuario.semanas_minimas}
                      onChange={(e) => setNovoUsuario({ ...novoUsuario, semanas_minimas: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Se a moto tiver menos semanas pagas no mês, será calculado proporcional ao lucro líquido.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="regras_mecanico"
                      checked={novoUsuario.regras_mecanico}
                      onChange={(e) => setNovoUsuario({ ...novoUsuario, regras_mecanico: e.target.checked })}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="regras_mecanico" className="cursor-pointer">
                      Aplicar regras especiais de mecânico
                      <span className="block text-xs text-gray-500 font-normal">
                        Mão de obra zero, óleo 100% locadora, peças 50/50 (ex: Carlos Humberto)
                      </span>
                    </Label>
                  </div>
                </>
              )}

              {novoUsuario.modelo_remuneracao === 'percentual' && (
                <>
                  <div>
                    <Label>Percentual de repasse (%) *</Label>
                    <Input
                      data-testid="input-percentual"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      required
                      value={novoUsuario.percentual_repasse}
                      onChange={(e) => setNovoUsuario({ ...novoUsuario, percentual_repasse: e.target.value })}
                      placeholder="75"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Aplicado sobre o lucro líquido (Receita − Despesas) das motos vinculadas. Valor sugerido: 70% a 80%.
                    </p>
                  </div>
                  <div>
                    <Label>Semanas mínimas pagas para entrar no fechamento *</Label>
                    <Input
                      type="number"
                      min="1"
                      max="52"
                      required
                      value={novoUsuario.semanas_minimas}
                      onChange={(e) => setNovoUsuario({ ...novoUsuario, semanas_minimas: e.target.value })}
                    />
                  </div>
                </>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
                💡 Adicione abaixo as despesas fixas que serão deduzidas no cálculo de lucro líquido deste investidor (rateadas entre as motos vinculadas).
              </div>
            </div>

            {/* === Despesas Fixas Personalizadas (criar) === */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-base">Despesas Fixas do Investidor</h4>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setNovoUsuario({
                    ...novoUsuario,
                    despesas_fixas_personalizadas: [
                      ...(novoUsuario.despesas_fixas_personalizadas || []),
                      { nome: '', valor: '', descricao: '' }
                    ]
                  })}
                >
                  + Adicionar
                </Button>
              </div>
              {(novoUsuario.despesas_fixas_personalizadas || []).length === 0 ? (
                <p className="text-xs text-gray-500 italic">
                  Nenhuma despesa adicionada. Use o botão &ldquo;+ Adicionar&rdquo; para incluir despesas como Seguro, Rastreador, Taxa ADM, etc.
                </p>
              ) : (
                <div className="space-y-2">
                  {(novoUsuario.despesas_fixas_personalizadas || []).map((d, idx) => (
                    <div key={idx} className="flex gap-2 items-start p-2 rounded border bg-gray-50">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input
                          data-testid={`input-despesa-nome-${idx}`}
                          placeholder="Nome (ex: Seguro)"
                          value={d.nome || ''}
                          onChange={(e) => {
                            const lista = [...(novoUsuario.despesas_fixas_personalizadas || [])];
                            lista[idx] = { ...lista[idx], nome: e.target.value };
                            setNovoUsuario({ ...novoUsuario, despesas_fixas_personalizadas: lista });
                          }}
                        />
                        <Input
                          data-testid={`input-despesa-valor-${idx}`}
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Valor R$"
                          value={d.valor || ''}
                          onChange={(e) => {
                            const lista = [...(novoUsuario.despesas_fixas_personalizadas || [])];
                            lista[idx] = { ...lista[idx], valor: e.target.value };
                            setNovoUsuario({ ...novoUsuario, despesas_fixas_personalizadas: lista });
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const lista = (novoUsuario.despesas_fixas_personalizadas || []).filter((_, i) => i !== idx);
                          setNovoUsuario({ ...novoUsuario, despesas_fixas_personalizadas: lista });
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalCriar(false)}>
                Cancelar
              </Button>
              <Button data-testid="btn-submit-criar-usuario" type="submit" disabled={loading}>
                {loading ? 'Criando...' : 'Criar Usuário'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Usuário */}
      <Dialog open={modalEditar} onOpenChange={setModalEditar}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Usuário — {usuarioEditando?.username}</DialogTitle>
          </DialogHeader>
          {usuarioEditando && (
            <form onSubmit={handleAtualizarUsuario} className="space-y-4">
              <div>
                <Label>Nome Completo</Label>
                <Input
                  value={usuarioEditando.nome_completo}
                  onChange={(e) => setUsuarioEditando({ ...usuarioEditando, nome_completo: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={usuarioEditando.email}
                  onChange={(e) => setUsuarioEditando({ ...usuarioEditando, email: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Nova Senha (deixe em branco para manter)</Label>
                <Input
                  type="password"
                  value={usuarioEditando.senha || ''}
                  onChange={(e) => setUsuarioEditando({ ...usuarioEditando, senha: e.target.value })}
                  placeholder="Nova senha (opcional)"
                />
              </div>
              
              <div>
                <Label>Observações</Label>
                <Input
                  value={usuarioEditando.observacoes || ''}
                  onChange={(e) => setUsuarioEditando({ ...usuarioEditando, observacoes: e.target.value })}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={usuarioEditando.ativo}
                  onChange={(e) => setUsuarioEditando({ ...usuarioEditando, ativo: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label>Usuário ativo</Label>
              </div>

              {/* === Dados de Cobrança PIX === */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-semibold text-base">Dados de Cobrança PIX</h4>
                <p className="text-xs text-gray-500">
                  Quando este sub-admin tiver locações compartilhadas, os lembretes de pagamento via WhatsApp usarão
                  estes dados em vez do PIX padrão da Desloca. Deixe os 3 campos em branco para usar o padrão.
                </p>
                <div>
                  <Label>Chave PIX</Label>
                  <Input
                    data-testid="input-sub-pix-chave"
                    placeholder="Ex.: meu@email.com, CPF, CNPJ ou aleatória"
                    value={usuarioEditando.dados_cobranca?.chave_pix || ''}
                    onChange={(e) => setUsuarioEditando({
                      ...usuarioEditando,
                      dados_cobranca: { ...(usuarioEditando.dados_cobranca || {}), chave_pix: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label>Nome do Beneficiário</Label>
                  <Input
                    data-testid="input-sub-pix-beneficiario"
                    placeholder="Nome que aparece para o cliente"
                    value={usuarioEditando.dados_cobranca?.nome_beneficiario || ''}
                    onChange={(e) => setUsuarioEditando({
                      ...usuarioEditando,
                      dados_cobranca: { ...(usuarioEditando.dados_cobranca || {}), nome_beneficiario: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label>Nome do Banco</Label>
                  <Input
                    data-testid="input-sub-pix-banco"
                    placeholder="Ex.: Nubank, Bradesco, Itaú"
                    value={usuarioEditando.dados_cobranca?.nome_banco || ''}
                    onChange={(e) => setUsuarioEditando({
                      ...usuarioEditando,
                      dados_cobranca: { ...(usuarioEditando.dados_cobranca || {}), nome_banco: e.target.value }
                    })}
                  />
                </div>
              </div>

              {/* === Modelo de Remuneração === */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-semibold text-base">Modelo de Remuneração</h4>

                <div>
                  <Label>Tipo de Modelo</Label>
                  <select
                    data-testid="select-modelo-edicao"
                    value={usuarioEditando.modelo_remuneracao || ''}
                    onChange={(e) => setUsuarioEditando({ ...usuarioEditando, modelo_remuneracao: e.target.value })}
                    className="w-full border rounded px-3 py-2 bg-white"
                  >
                    <option value="">— não definido —</option>
                    <option value="fixo">Pagamento fixo por moto</option>
                    <option value="percentual">Percentual sobre faturamento líquido</option>
                  </select>
                </div>

                {usuarioEditando.modelo_remuneracao === 'fixo' && (
                  <>
                    <div>
                      <Label>Valor fixo por moto (R$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={usuarioEditando.valor_fixo_por_moto ?? ''}
                        onChange={(e) => setUsuarioEditando({ ...usuarioEditando, valor_fixo_por_moto: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Semanas mínimas pagas</Label>
                      <Input
                        type="number"
                        min="1"
                        max="52"
                        value={usuarioEditando.semanas_minimas ?? 4}
                        onChange={(e) => setUsuarioEditando({ ...usuarioEditando, semanas_minimas: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!usuarioEditando.regras_mecanico}
                        onChange={(e) => setUsuarioEditando({ ...usuarioEditando, regras_mecanico: e.target.checked })}
                        className="h-4 w-4"
                      />
                      <Label className="cursor-pointer">
                        Regras especiais de mecânico (mão de obra zero, peças 50/50)
                      </Label>
                    </div>
                  </>
                )}

                {usuarioEditando.modelo_remuneracao === 'percentual' && (
                  <>
                    <div>
                      <Label>Percentual de repasse (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={usuarioEditando.percentual_repasse ?? ''}
                        onChange={(e) => setUsuarioEditando({ ...usuarioEditando, percentual_repasse: e.target.value })}
                        placeholder="75"
                      />
                    </div>
                    <div>
                      <Label>Semanas mínimas pagas</Label>
                      <Input
                        type="number"
                        min="1"
                        max="52"
                        value={usuarioEditando.semanas_minimas ?? 4}
                        onChange={(e) => setUsuarioEditando({ ...usuarioEditando, semanas_minimas: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* === Despesas fixas personalizadas do usuário === */}
              {usuarioEditando.modelo_remuneracao && (
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-base">Despesas Fixas do Investidor</h4>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setUsuarioEditando({
                        ...usuarioEditando,
                        despesas_fixas_personalizadas: [
                          ...(usuarioEditando.despesas_fixas_personalizadas || []),
                          { nome: '', valor: '', descricao: '' }
                        ]
                      })}
                    >
                      + Adicionar
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Estas despesas serão deduzidas no cálculo de lucro líquido (rateadas entre as motos vinculadas).
                  </p>

                  {(usuarioEditando.despesas_fixas_personalizadas || []).length === 0 ? (
                    <p className="text-xs text-gray-500 italic">
                      Nenhuma despesa fixa cadastrada. Use &ldquo;+ Adicionar&rdquo; para incluir.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {(usuarioEditando.despesas_fixas_personalizadas || []).map((d, idx) => (
                        <div key={idx} className="flex gap-2 items-start p-2 rounded border bg-gray-50">
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <Input
                              data-testid={`edit-despesa-nome-${idx}`}
                              placeholder="Nome (ex: Seguro)"
                              value={d.nome || ''}
                              onChange={(e) => {
                                const lista = [...(usuarioEditando.despesas_fixas_personalizadas || [])];
                                lista[idx] = { ...lista[idx], nome: e.target.value };
                                setUsuarioEditando({ ...usuarioEditando, despesas_fixas_personalizadas: lista });
                              }}
                            />
                            <Input
                              data-testid={`edit-despesa-valor-${idx}`}
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Valor R$"
                              value={d.valor || ''}
                              onChange={(e) => {
                                const lista = [...(usuarioEditando.despesas_fixas_personalizadas || [])];
                                lista[idx] = { ...lista[idx], valor: e.target.value };
                                setUsuarioEditando({ ...usuarioEditando, despesas_fixas_personalizadas: lista });
                              }}
                            />
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const lista = (usuarioEditando.despesas_fixas_personalizadas || []).filter((_, i) => i !== idx);
                              setUsuarioEditando({ ...usuarioEditando, despesas_fixas_personalizadas: lista });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {(usuarioEditando.despesas_fixas_personalizadas || []).length > 0 && (
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200 text-sm font-semibold">
                      <span>Total mensal:</span>
                      <span className="text-emerald-600">
                        R$ {(usuarioEditando.despesas_fixas_personalizadas || [])
                          .reduce((sum, d) => sum + parseFloat(d.valor || 0), 0)
                          .toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setModalEditar(false)}>
                  Cancelar
                </Button>
                <Button data-testid="btn-submit-editar-usuario" type="submit" disabled={loading}>
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Compartilhar Locações */}
      <Dialog open={modalCompartilhar} onOpenChange={setModalCompartilhar}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Compartilhar Locações - {usuarioCompartilhando?.nome_completo}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Permissões de Visualização:</strong> Selecione as locações que <strong>{usuarioCompartilhando?.nome_completo}</strong> poderá visualizar.
                O sub-admin verá apenas os dados das locações selecionadas (pagamentos, receitas, manutenções, clientes e motos relacionados).
              </p>
            </div>
            
            <div className="text-sm text-gray-600">
              <strong>{locacoesCompartilhadas.length}</strong> locação(ões) compartilhada(s)
            </div>
            
            {locacoes.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                Nenhuma locação disponível
              </div>
            ) : (
              <div className="space-y-2">
                {locacoes.map((locacao) => {
                  const compartilhada = locacaoEstaCompartilhada(locacao.id);
                  
                  return (
                    <div
                      key={locacao.id}
                      className={`p-4 rounded-lg border ${
                        compartilhada ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-semibold">
                            Cliente: {locacao.cliente_nome || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Moto: {locacao.moto_placa || 'N/A'} • 
                            Status: {locacao.status} • 
                            Início: {new Date(locacao.data_inicio).toLocaleDateString()}
                          </div>
                        </div>
                        
                        {compartilhada ? (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRemoverCompartilhamento(locacao.id)}
                            disabled={loading}
                            className="flex items-center gap-1"
                          >
                            <X className="h-4 w-4" />
                            Remover
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleCompartilharLocacao(locacao.id)}
                            disabled={loading}
                            className="flex items-center gap-1"
                          >
                            <Share2 className="h-4 w-4" />
                            Compartilhar
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setModalCompartilhar(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsuariosSistemaPage;
